import { ArrowRightIcon } from '@f8n/icons';
import { useRouter } from 'next/router';
import type { Address } from 'viem';
import { useWriteContract, useSimulateContract } from 'wagmi';

import { mapToLaunchMediaAsset } from 'components/MediaStack';
import SmallArtworkCard from 'components/SmallArtworkCard';
import TransactionModal from 'components/TransactionModal';
import TransactionModalContent from 'components/TransactionModalContent';
import Button from 'components/base/Button';
import InputEth from 'components/base/InputEth';
import Modal from 'components/base/Modal';
import Text from 'components/base/Text';
import SuccessModal from 'components/modals/SuccessModal';
import AmountBreakdown from 'components/transactions/generic/AmountBreakdown';
import useTransactionStore from 'state/stores/transactions';

import useBuyTweetText from 'hooks/use-buy-tweet-text';
import useModalVisibility from 'hooks/use-modal-visibility';
import { useExhibitionIdForNft } from 'hooks/web3/use-exhibition-id-for-nft';
import { useGetBuyPrice } from 'hooks/web3/use-get-buy-price';
import useMarketBalances from 'hooks/web3/use-market-balances';
import { useNftSeller } from 'hooks/web3/use-nft-seller';
import useReferral from 'hooks/web3/use-referral';
import { NFTMarket } from 'lib/abis/NFTMarket';
import { getContractAddress } from 'lib/addresses';
import { ChainId } from 'lib/chains';
import { ZERO_ADDRESS } from 'lib/constants';
import { getAddress } from 'utils/address';
import { buildImageUrlFromAnyAsset } from 'utils/assets';
import { formatETHWithSuffix } from 'utils/formatters';
import { getTokenFilter } from 'utils/inputs';
import { extractPrepareContractWriteRevertReason } from 'utils/revert-reasons';
import { getPath } from 'utils/router';
import { calculateTransactionValue } from 'utils/transactions';
import { formatEther } from 'utils/units';

import { NftFilter } from 'types/Nft';
import { ModalOfType } from 'types/modal';

const MODAL_KEY = 'BUY_NOW';
const ACTION = 'accept-buy-now';

type BuyNowModalOptions = ModalOfType<typeof MODAL_KEY>;
export default function BuyNowModal() {
  const modal = useModalVisibility(MODAL_KEY);

  return (
    <Modal.Root open={modal.open} onOpenChange={modal.onOpenChange}>
      <Modal.Portal>
        <Modal.BlurOverlay />
        <Modal.PositionOverlay>
          <Modal.UnmountListener onUnmount={modal.onUnmount} />
          {modal.config && <BuyNowModalWindow {...modal.config} />}
        </Modal.PositionOverlay>
      </Modal.Portal>
    </Modal.Root>
  );
}

function BuyNowModalWindow(props: BuyNowModalOptions) {
  const router = useRouter();
  const txStore = useTransactionStore();

  const { nftFilter, slug, username, publicKey } = getNormalizedValues(props);

  const getBuyPriceQuery = useGetBuyPrice(nftFilter);

  const marketBalancesQuery = useMarketBalances(nftFilter);

  const referralAddress = useReferral({
    contractAddress: nftFilter.contractAddress,
  });

  const getTransactionValue = (transactionValue: bigint) => {
    if (marketBalancesQuery.data) {
      return calculateTransactionValue({
        transactionValue,
        marketplaceBalance: marketBalancesQuery.data.availableFethBalance,
      });
    } else {
      return transactionValue;
    }
  };

  const simulateBuyNow = useSimulateContract({
    abi: NFTMarket,
    functionName: 'buyV2',
    address: getContractAddress({
      chainId: nftFilter.chainId,
      contractName: 'nftMarket',
    }),
    chainId: nftFilter.chainId,
    args: getBuyPriceQuery.isSuccess
      ? [
          nftFilter.contractAddress,
          BigInt(nftFilter.tokenId),
          getBuyPriceQuery.data.price,
          referralAddress ? getAddress(referralAddress) : ZERO_ADDRESS,
        ]
      : undefined,
    query: {
      enabled: getBuyPriceQuery.isSuccess,
      retry: false,
    },
    value: getBuyPriceQuery.isSuccess
      ? getTransactionValue(getBuyPriceQuery.data.price)
      : undefined,
  });

  const nftSellerQuery = useNftSeller(nftFilter);

  const exhibitionByNft = useExhibitionIdForNft(
    {
      nftFilter,
      sellerPublicKey: nftSellerQuery.data || ZERO_ADDRESS,
    },
    { enabled: Boolean(nftSellerQuery.data) }
  );

  const contractWrite = useWriteContract({
    mutation: {
      onSuccess: (txHash) => {
        txStore.startTracking({
          chainId: nftFilter.chainId,
          ui: 'toast',
          action: {
            name: ACTION,
            worldId: exhibitionByNft.data
              ? Number(exhibitionByNft.data.worldId)
              : null,
          },
          txHash,
          title: {
            PENDING: 'Buying…',
            SUCCESS: 'You got it!',
          },
          description: {
            PENDING: `The sale is being confirmed on the blockchain. You can close this if you like.`,
            SUCCESS: getBuyPriceQuery.isSuccess
              ? `You bought the NFT for ${formatETHWithSuffix(
                  formatEther(getBuyPriceQuery.data.price)
                )}.`
              : undefined,
          },
        });
      },
    },
  });

  const twitterShareText = useBuyTweetText(
    props.version === 1
      ? {
          creatorName: props.artwork.creator?.name ?? null,
          creatorUsername: props.artwork.creator?.username ?? null,
          name: props.artwork.name,
          publicKey: props.artwork.publicKey,
        }
      : {
          creatorName: props.nft.creator.name,
          creatorUsername: props.nft.creator.username,
          name: props.nft.name,
          publicKey: props.nft.creator.publicKey,
        }
  );

  const assetUrl =
    props.version === 1 ? buildImageUrlFromAnyAsset(props.artwork) : undefined;

  const media =
    assetUrl && props.version === 1
      ? mapToLaunchMediaAsset(
          { assetUrl, mimeType: props.artwork.mimeType || 'image/png' },
          { imageOptions: { q: 70, h: 640, w: 640 } }
        )
      : null;

  const artworkPath = getPath.nft.page({
    usernameOrAddress: username || publicKey,
    collectionSlugOrAddress: slug || nftFilter.contractAddress,
    tokenId: nftFilter.tokenId,
  });

  return (
    <TransactionModal
      chainId={nftFilter.chainId}
      preSignPrompt={{
        type: 'skip',
      }}
    >
      <TransactionModalContent
        action={ACTION}
        chainId={nftFilter.chainId}
        txHash={contractWrite.data || null}
        footer={
          <Modal.Footer>
            <TransactionModal.TransactionButton
              chainId={nftFilter.chainId}
              isLoading={contractWrite.isPending || Boolean(contractWrite.data)}
              isDisabled={!simulateBuyNow.isSuccess}
              write={() => {
                if (simulateBuyNow.isSuccess) {
                  contractWrite.writeContract(simulateBuyNow.data.request);
                }
              }}
              error={extractPrepareContractWriteRevertReason(simulateBuyNow)}
              label="Confirm"
            />
          </Modal.Footer>
        }
        renderOnSuccess={() => ({
          body: (
            <SuccessModal.Body
              media={media}
              title="You got it!"
              description={
                <>
                  You bought the NFT for{' '}
                  {getBuyPriceQuery.isSuccess && (
                    <Text as="span" weight="semibold" color="strong">
                      {formatETHWithSuffix(
                        formatEther(getBuyPriceQuery.data.price)
                      )}
                    </Text>
                  )}
                  . It'll be available on your profile instantly!
                </>
              }
            />
          ),
          footer: (
            <SuccessModal.Footer
              shareText={twitterShareText}
              extraCta={
                artworkPath && (
                  <Button
                    icon
                    as={Modal.Close}
                    onClick={() => router.push(artworkPath)}
                  >
                    <span>View NFT</span>
                    <ArrowRightIcon />
                  </Button>
                )
              }
            />
          ),
        })}
      >
        <TransactionModal.Body css={{ gap: '$6' }}>
          <Modal.BodyTitle
            align="left"
            title="Buy Now"
            description="Once the transaction is confirmed, the NFT will be sent to your wallet instantly."
          />
          {props.version === 1 ? (
            <SmallArtworkCard.Legacy artwork={props.artwork} />
          ) : (
            <SmallArtworkCard.MarketNft nft={props.nft} />
          )}
          <TransactionModal.InputWrapper>
            <InputEth
              id="price"
              disabled
              value={
                getBuyPriceQuery.isSuccess
                  ? formatEther(getBuyPriceQuery.data.price)
                  : ''
              }
            />
            <AmountBreakdown.Available />
          </TransactionModal.InputWrapper>
        </TransactionModal.Body>
      </TransactionModalContent>
    </TransactionModal>
  );
}

interface NormalizedValues {
  username: string | null;
  publicKey: string;
  nftFilter: NftFilter;
  slug?: string;
}

const getNormalizedValues = (options: BuyNowModalOptions): NormalizedValues => {
  if (options.version === 1) {
    const { artwork } = options;
    return {
      slug: artwork.collection?.slug ?? undefined,
      nftFilter: {
        chainId: artwork.chainId as ChainId,
        contractAddress: artwork.contractAddress ?? ('' as Address),
        tokenId: Number(artwork.tokenId),
      },
      username: artwork.creator?.username || null,
      publicKey: artwork.creator?.publicKey ?? '',
    };
  }

  const { nft } = options;

  return {
    nftFilter: getTokenFilter(nft),
    username: nft.creator?.username,
    publicKey: nft.creator?.publicKey,
  };
};
